'use client';

import { useCallback, useRef, useState } from 'react';

import { apiClient } from '@/lib/api/client';

export type PlanDuration = 'monthly' | 'quarterly' | 'yearly';

type PlanFeature = {
  title: string;
  description?: string;
  is_included: boolean;
};

type PublicPlan = {
  plan_type?: string;
  title?: string;
  price?: string | number;
  original_price?: string | number;
  currency?: string;
  currency_code?: string;
  allowed_ai_tokens?: number;
  ui_config?: {
    subtitle?: string;
    features?: PlanFeature[];
  };
};

type PublicPricingResponse = {
  status: string;
  duration_type?: PlanDuration;
  plan_message?: string;
  plans?: PublicPlan[];
  free_trial?: PublicPlan;
  message?: string;
};

function creditLabel(plan: PublicPlan | undefined): string {
  const amount = Number(plan?.allowed_ai_tokens);
  if (!Number.isFinite(amount) || amount <= 0) {
    return '';
  }
  return `${amount.toLocaleString('en-US')} AI credits`;
}

export function usePublicPricing() {
  const [planDuration, setPlanDuration] = useState<PlanDuration>('monthly');
  const isIndiaPlan = useRef(false);

  const populatePricingPlan = useCallback(
    (plan: PublicPlan, durationType?: PlanDuration, planMessage?: string) => {
      const paidPlanPricing = document.getElementById('paid-plan-pricing');
      if (!paidPlanPricing) {
        return;
      }

      const planOriginalPrice = paidPlanPricing.querySelector('.plan-og-price');
      const planPrice = paidPlanPricing.querySelector('.plan-price');
      const savedAmount = paidPlanPricing.querySelector('.saved-amount');
      const planTitle = paidPlanPricing.querySelector('.plan-title');
      const planMessageElement = document.getElementById('plan-message');

      if (plan.original_price && planOriginalPrice) {
        planOriginalPrice.textContent = `${plan.currency_code || ''}${plan.original_price}`;
      }
      if (plan.price && planPrice) {
        planPrice.textContent = `${plan.currency_code || ''}${plan.price}`;
      }

      const original = Number(String(plan.original_price || '').replace(/,/g, ''));
      const offer = Number(String(plan.price || '').replace(/,/g, ''));
      if (savedAmount instanceof HTMLElement) {
        if (original > offer && original > 0) {
          const discountPercent = Math.round(((original - offer) / original) * 100);
          savedAmount.textContent = `SAVE ${discountPercent}%`;
          savedAmount.style.display = 'flex';
        } else {
          if (planOriginalPrice) {
            planOriginalPrice.textContent = '';
          }
          savedAmount.style.display = 'none';
        }
      }

      if (plan.title && planTitle) {
        planTitle.textContent = plan.title;
      }

      const perPriceEl = paidPlanPricing.querySelector('.per-price');
      if (perPriceEl) {
        const effectiveDuration = durationType || planDuration;
        let months: number | null = null;
        if (effectiveDuration === 'yearly') {
          months = 12;
        } else if (effectiveDuration === 'quarterly') {
          months = 3;
        }

        const parent = perPriceEl.parentElement;
        if (months && plan.price != null && plan.price !== '') {
          const numericPrice = Number(String(plan.price).replace(/,/g, ''));
          if (!Number.isNaN(numericPrice)) {
            perPriceEl.textContent = `just ${plan.currency_code || ''}${Math.floor(numericPrice / months)} per month`;
            if (parent) {
              parent.style.display = 'block';
            }
          }
        } else if (parent) {
          perPriceEl.textContent = '';
          parent.style.display = 'none';
        }
      }

      const mostPopularEl = document.getElementById('most-popular');
      const bestValueEl = document.getElementById('best-value');
      const effectiveDuration = durationType || planDuration;
      if (mostPopularEl) {
        mostPopularEl.style.display =
          effectiveDuration === 'quarterly' ? 'inline' : 'none';
      }
      if (bestValueEl) {
        bestValueEl.style.display =
          effectiveDuration === 'yearly' ? 'inline' : 'none';
      }

      if (plan.currency === 'INR') {
        isIndiaPlan.current = true;
      }

      const indiaTabs = document.getElementById('plan-duration-tabs-india');
      const nonIndiaTabs = document.getElementById('plan-duration-tabs-non-india');
      if (isIndiaPlan.current) {
        if (planTitle instanceof HTMLElement) {
          planTitle.textContent = '';
          planTitle.style.display = 'none';
        }
        if (indiaTabs) {
          indiaTabs.style.display = 'flex';
        }
        if (nonIndiaTabs) {
          nonIndiaTabs.style.display = 'none';
        }
      } else {
        if (indiaTabs) {
          indiaTabs.style.display = 'none';
        }
        if (nonIndiaTabs) {
          nonIndiaTabs.style.display = 'flex';
        }
      }

      if (planMessage && planMessage !== '' && planMessageElement) {
        planMessageElement.innerHTML = `<span>${planMessage}</span>`;
        planMessageElement.style.display = 'flex';
      } else if (planMessageElement) {
        planMessageElement.style.display = 'none';
      }

      const planDurationTabs = paidPlanPricing.querySelectorAll('.tab');
      if (planDurationTabs.length > 0 && durationType) {
        planDurationTabs.forEach((tab) => {
          if (tab.classList.contains(`${durationType}-tab`)) {
            tab.classList.add('active');
          } else {
            tab.classList.remove('active');
          }
        });
      }

      const paidPlanFeatureList = document.getElementById('paid-plan-feature-list');
      if (paidPlanFeatureList && plan.ui_config?.features) {
        paidPlanFeatureList.innerHTML = '';
        plan.ui_config.features.forEach((feature) => {
          const li = document.createElement('li');
          const div = document.createElement('div');
          const icon = document.createElement('i');
          const span = document.createElement('span');
          icon.classList.add(
            'fa-solid',
            feature.is_included ? 'fa-check' : 'fa-xmark',
          );
          div.appendChild(icon);
          span.textContent =
            feature.description && feature.description !== ''
              ? `${feature.title} - ${feature.description}`
              : feature.title;
          li.appendChild(div);
          li.appendChild(span);
          paidPlanFeatureList.appendChild(li);
        });
      }
    },
    [planDuration],
  );

  const fetchPricing = useCallback(
    async (duration: PlanDuration = planDuration) => {
      try {
        const { data } = await apiClient.post<PublicPricingResponse>(
          '/pro/plans/public',
          { catalog: 'export' },
        );
        void duration;

        if (data.status !== 'success') {
          return;
        }

        const shortPrice = document.getElementById('export-short-price');
        const longPrice = document.getElementById('export-long-price');
        const plans = data.plans || [];
        const shortPlan =
          plans.find((plan) => plan.currency && plan.title) || plans[0];
        const priced = plans.map((plan) => {
          const symbol = plan.currency_code || '';
          const amount = plan.price != null ? String(plan.price) : '';
          return `${symbol}${amount}`;
        });
        if (shortPrice && priced[0]) {
          shortPrice.textContent = priced[0];
        }
        if (longPrice && priced[1]) {
          longPrice.textContent = priced[1];
        }
        const shortCredits = document.getElementById('export-short-credits');
        const longCredits = document.getElementById('export-long-credits');
        const shortOffer = plans.find(
          (plan) => plan.plan_type === 'all_features_14_day_pass_plan',
        );
        const longOffer = plans.find((plan) => plan.plan_type === 'regular_plan');
        if (shortCredits) {
          shortCredits.textContent = creditLabel(shortOffer);
        }
        if (longCredits) {
          longCredits.textContent = creditLabel(longOffer);
        }
        if (shortPlan) {
          populatePricingPlan(shortPlan, data.duration_type, data.plan_message);
        }
      } catch {
        // Pricing is non-blocking on the marketing home page.
      }
    },
    [planDuration, populatePricingPlan],
  );

  const changePlanDuration = useCallback(
    (duration: PlanDuration) => {
      setPlanDuration(duration);
      void fetchPricing(duration);
    },
    [fetchPricing],
  );

  return {
    planDuration,
    changePlanDuration,
    fetchPricing,
  };
}
